import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { AdminPickupRoster } from './AdminPickupRoster';
import { AdminDayRoster } from './AdminDayRoster';
import { AdminAttendanceHistory } from './AdminAttendanceHistory';
import { trackEvent, analytics } from '../../utils/analytics';
import { AdminLayout } from '../AdminLayout';
import { formatDateDisplay, formatDateInBusinessTZ } from '../../utils/date';
import { Modal } from '../../components/Modal';
import { CsvImport } from '../../components/CsvImport';
import { StudentPhotoImport } from '../components/StudentPhotoImport';
import { PickupContactsModal } from '../components/PickupContactsModal';
import { WaiverPrintPreview } from '../../components/WaiverPrintPreview';
import { BlankWaiverPrintPreview } from '../../components/BlankWaiverPrintPreview';
import { StudentContactsEditor } from '../../components/StudentContactsEditor';
import { getAllSchools, grades } from '../../config/schools';
import { sanitizeRoomNumberInput, toStoredRoomNumber } from '../../types/roomNumber';
import { Copy, Edit, PowerOff, Power, Printer, Download, Trash2, Gift, UserCheck } from 'lucide-react';
import { getProgramTypeIcon } from '../../types/program';
import type { BhcsStudentWaiver, BhcsWaiverMetadata } from '../../types/waiver';
import { isBhcsStudentWaiverOptOut, isBhcsStudentWaiverSigned } from '../../types/waiver';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { STUDENT_PARAM, useFocusedRecordId } from '../utils/recordLinks';
import { FocusedRecordBanner } from '../components/FocusedRecordBanner';
import { FosterCityResidentBadge } from '../components/FosterCityResidentBadge';
import { isFosterCityZip } from '../../utils/eligibilityDiscounts';


interface StudentMetadata {
  school?: string;
  grade?: string;
  /** Digits-only classroom number; kept as a string so "013" survives. */
  room_number?: string;
  labels?: string[];
  deposit_badge?: { amount: number };
  bhcs_waiver?: unknown;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  metadata?: StudentMetadata;
  active: boolean;
  no_charge?: boolean;
  created_at: string;
  student_parents: {
    parent: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      postal_code?: string | null;
    };
    relationship: string;
    is_primary: boolean;
  }[];
  active_enrollments?: Array<{
    program_name: string;
    program_type: string;
    created_at: string;
  }>;
}

/** A parent with ZIP 94404 makes the student a Foster City Resident (Kaleido discount). */
function isFosterCityResident(student: Student): boolean {
  return (student.student_parents ?? []).some((sp) => isFosterCityZip(sp.parent?.postal_code));
}

type StudentsTab = 'directory' | 'roster' | 'day' | 'history' | 'photos';
const STUDENTS_TABS: readonly StudentsTab[] = ['directory', 'roster', 'day', 'history', 'photos'];

/**
 * Sub-tabs across the top of the Students page. The active tab lives in the query
 * string so a roster view can be linked, bookmarked, and survives a refresh — an admin
 * running dismissal reloads this screen a lot.
 */
function StudentsTabs({
  active,
  onChange,
}: {
  active: StudentsTab;
  onChange: (tab: StudentsTab) => void;
}) {
  const tabs: { key: StudentsTab; label: string }[] = [
    { key: 'directory', label: 'All students' },
    { key: 'roster', label: 'Pickup roster' },
    { key: 'day', label: 'Day roster' },
    { key: 'history', label: 'History' },
    { key: 'photos', label: 'Headshots' },
  ];

  return (
    <div className="flex gap-1 px-4 sm:px-6 border-b border-gray-200 bg-white print:hidden">
      {tabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          aria-current={active === tab.key ? 'page' : undefined}
          className={`px-3 py-2.5 text-sm border-b-2 -mb-px focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
            active === tab.key
              ? 'border-blue-600 text-blue-600 font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface CleanupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  studentName: string;
  isLoading: boolean;
}

interface EditStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  onSave: (studentData: {
    first_name: string;
    last_name: string;
    date_of_birth: string | null;
    metadata: { school?: string; grade?: string; room_number?: string; labels?: string[]; deposit_badge?: { amount: number } };
    relationshipsToRemove: string[];
    relationshipsToAdd: Array<{
      parent_id: string;
      relationship: string;
      is_primary: boolean;
    }>;
  }) => Promise<void>;
  isLoading: boolean;
}

interface ParentSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

function CleanupModal({ isOpen, onClose, onConfirm, studentName, isLoading }: CleanupModalProps) {
  const [confirmationText, setConfirmationText] = useState('');
  const isConfirmEnabled = confirmationText === studentName;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirm Student Cleanup">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Student Cleanup</h3>
        <div className="text-sm text-gray-500 mb-4">
          <p>You are about to delete <span className="font-semibold">{studentName}</span> and all associated data including:</p>
          <ul className="list-disc list-inside mt-2">
            <li>Enrollments</li>
            <li>Invoices and line items</li>
            <li>Payments and refunds</li>
            <li>Parent relationships</li>
          </ul>
          <p className="mt-2 text-red-600">This action cannot be undone.</p>
        </div>
        <div className="mb-4">
          <label htmlFor="confirmationText" className="block text-sm font-medium text-gray-700 mb-1">
            Type "{studentName}" to confirm deletion
          </label>
          <input
            type="text"
            id="confirmationText"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder={`Type "${studentName}" here`}
            disabled={isLoading}
          />
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!isConfirmEnabled || isLoading}
            className={`px-4 py-2 rounded-md text-white transition-colors ${
              isConfirmEnabled && !isLoading
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? 'Cleaning up...' : 'Delete Student'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditStudentModal({ isOpen, onClose, student, onSave, isLoading }: EditStudentModalProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    school: '',
    grade: '',
    roomNumber: '',
    labels: [] as string[],
    depositAmount: '',
  });
  const [labelInput, setLabelInput] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [currentRelationships, setCurrentRelationships] = useState<Student['student_parents']>([]);
  const [relationshipsToRemove, setRelationshipsToRemove] = useState<string[]>([]);
  const [relationshipsToAdd, setRelationshipsToAdd] = useState<Array<{
    parent_id: string;
    parent_info: ParentSearchResult;
    relationship: string;
    is_primary: boolean;
  }>>([]);
  const [parentSearch, setParentSearch] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState<ParentSearchResult[]>([]);
  const [searchingParents, setSearchingParents] = useState(false);
  const [showAddParent, setShowAddParent] = useState(false);

  const schoolsList = getAllSchools();

  // Update form data when student prop changes
  useEffect(() => {
    if (student) {
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        date_of_birth: student.date_of_birth || '',
        school: student.metadata?.school || '',
        grade: student.metadata?.grade || '',
        roomNumber: student.metadata?.room_number || '',
        labels: student.metadata?.labels ? [...student.metadata.labels] : [],
        depositAmount:
          typeof student.metadata?.deposit_badge?.amount === 'number'
            ? String(student.metadata.deposit_badge.amount)
            : '',
      });
      setCurrentRelationships(student.student_parents || []);
      setRelationshipsToRemove([]);
      setRelationshipsToAdd([]);
      setParentSearch('');
      setParentSearchResults([]);
      setShowAddParent(false);
      setFormError(null);
      setLabelInput('');
    }
  }, [student]);

  // Search for parents
  const searchParents = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setParentSearchResults([]);
      return;
    }

    try {
      setSearchingParents(true);
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .eq('active', true)
        .limit(10);

      if (error) throw error;

      // Filter out parents already associated or about to be added
      const existingParentIds = new Set([
        ...currentRelationships.filter(r => !relationshipsToRemove.includes(r.parent.id)).map(r => r.parent.id),
        ...relationshipsToAdd.map(r => r.parent_id)
      ]);

      const filteredResults = (data || []).filter(parent => !existingParentIds.has(parent.id));
      setParentSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching parents:', error);
    } finally {
      setSearchingParents(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (parentSearch) {
        searchParents(parentSearch);
      } else {
        setParentSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [parentSearch]);

  const handleRemoveRelationship = (parentId: string) => {
    setRelationshipsToRemove(prev => [...prev, parentId]);
  };

  const handleUndoRemoveRelationship = (parentId: string) => {
    setRelationshipsToRemove(prev => prev.filter(id => id !== parentId));
  };

  const handleAddParentRelationship = (parent: ParentSearchResult, relationship: string = 'Parent', isPrimary: boolean = false) => {
    setRelationshipsToAdd(prev => [...prev, {
      parent_id: parent.id,
      parent_info: parent,
      relationship,
      is_primary: isPrimary
    }]);
    setParentSearch('');
    setParentSearchResults([]);
    setShowAddParent(false);
  };

  const handleRemoveAddedRelationship = (parentId: string) => {
    setRelationshipsToAdd(prev => prev.filter(r => r.parent_id !== parentId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Basic validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('First name and last name are required');
      return;
    }

    // Deposit badge: blank clears it, otherwise must be a positive number
    const trimmedDeposit = formData.depositAmount.trim();
    let depositBadge: { amount: number } | undefined;
    if (trimmedDeposit) {
      const parsedDeposit = Number(trimmedDeposit);
      if (!Number.isFinite(parsedDeposit) || parsedDeposit <= 0) {
        setFormError('Deposit amount must be a positive number');
        return;
      }
      depositBadge = { amount: Math.round(parsedDeposit * 100) / 100 };
    }

    try {
      await onSave({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        date_of_birth: formData.date_of_birth,
        metadata: {
          school: formData.school || undefined,
          grade: formData.grade || undefined,
          room_number: toStoredRoomNumber(formData.roomNumber),
          labels: formData.labels.length > 0 ? formData.labels : undefined,
          deposit_badge: depositBadge,
        },
        relationshipsToRemove,
        relationshipsToAdd: relationshipsToAdd.map(rel => ({
          parent_id: rel.parent_id,
          relationship: rel.relationship,
          is_primary: rel.is_primary
        }))
      });
    } catch (error: unknown) {
      console.error('Error saving student:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const normalizeLabel = (value: string) => value.trim();

  const handleAddLabel = () => {
    const normalized = normalizeLabel(labelInput);
    if (!normalized) return;
    const normalizedKey = normalized.toLowerCase();
    const existing = new Set(formData.labels.map(label => label.toLowerCase()));
    if (existing.has(normalizedKey)) {
      setLabelInput('');
      return;
    }
    setFormData(prev => ({ ...prev, labels: [...prev.labels, normalized] }));
    setLabelInput('');
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.filter(label => label !== labelToRemove),
    }));
  };

  if (!student) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Student">
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.date_of_birth}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
              School
            </label>
            <select
              id="school"
              name="school"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.school}
              onChange={handleInputChange}
              disabled={isLoading}
            >
              <option value="">Select a school</option>
              {schoolsList.map((school: string) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
              Grade
            </label>
            <select
              id="grade"
              name="grade"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.grade}
              onChange={handleInputChange}
              disabled={isLoading}
            >
              <option value="">Select a grade</option>
              {grades.map((grade) => (
                <option key={grade.value} value={grade.value}>
                  {grade.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="roomNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Room Number
            </label>
            {/* type="text" with a numeric keypad, not type="number": a number input would
                drop the leading zero from a room written as "013". */}
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              id="roomNumber"
              name="roomNumber"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.roomNumber}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, roomNumber: sanitizeRoomNumberInput(e.target.value) }))
              }
              placeholder="e.g. 013"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">Optional. Digits only.</p>
          </div>

          <div>
            <label htmlFor="labels" className="block text-sm font-medium text-gray-700 mb-1">
              Labels
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.labels.length > 0 ? (
                formData.labels.map((label) => (
                  <span key={label} className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-medium">
                    {label}
                    <button
                      type="button"
                      onClick={() => handleRemoveLabel(label)}
                      className="text-blue-700 hover:text-blue-900"
                      aria-label={`Remove label ${label}`}
                      disabled={isLoading}
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">No labels yet</span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                id="labels"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLabel();
                  }
                }}
                placeholder="Add a label and press Enter"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={handleAddLabel}
                className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                Add
              </button>
            </div>
          </div>

          {/* Deposit badge */}
          <div>
            <label htmlFor="depositAmount" className="block text-sm font-medium text-gray-700 mb-1">
              Deposit badge
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                id="depositAmount"
                name="depositAmount"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.depositAmount}
                onChange={handleInputChange}
                placeholder="0.00"
                disabled={isLoading}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Shows a deposit badge with this amount on the parent&rsquo;s student page. Leave blank to remove the badge.
            </p>
            {formData.depositAmount.trim() && Number(formData.depositAmount) > 0 && (
              <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-medium">
                💰 Deposit ${Number(formData.depositAmount).toFixed(2)}
              </span>
            )}
          </div>

          {/* Parent Relationships */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Parent Relationships</h4>
            
            {/* Current Relationships */}
            <div className="space-y-2 mb-4">
              {currentRelationships.map((rel, index) => {
                const isBeingRemoved = relationshipsToRemove.includes(rel.parent.id);
                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 border rounded-md ${
                      isBeingRemoved ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {rel.parent.first_name} {rel.parent.last_name}
                        {rel.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                      </div>
                      <div className="text-xs text-gray-500">{rel.parent.email}</div>
                      <div className="text-xs text-gray-500">{rel.relationship}</div>
                    </div>
                    <div className="flex space-x-2">
                      {isBeingRemoved ? (
                        <button
                          type="button"
                          onClick={() => handleUndoRemoveRelationship(rel.parent.id)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          disabled={isLoading}
                        >
                          Undo
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemoveRelationship(rel.parent.id)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* New Relationships to Add */}
            {relationshipsToAdd.map((rel, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-md mb-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {rel.parent_info.first_name} {rel.parent_info.last_name}
                    {rel.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                  </div>
                  <div className="text-xs text-gray-500">{rel.parent_info.email}</div>
                  <div className="text-xs text-gray-500">{rel.relationship}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAddedRelationship(rel.parent_id)}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                  disabled={isLoading}
                >
                  Remove
                </button>
              </div>
            ))}

            {/* Add Parent Section */}
            {!showAddParent ? (
              <button
                type="button"
                onClick={() => setShowAddParent(true)}
                className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800 rounded-md transition-colors"
                disabled={isLoading}
              >
                + Add Parent Relationship
              </button>
            ) : (
              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="text-sm font-medium text-gray-700">Add Parent</h5>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddParent(false);
                      setParentSearch('');
                      setParentSearchResults([]);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
                
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={parentSearch}
                    onChange={(e) => setParentSearch(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {searchingParents && (
                  <div className="text-sm text-gray-500 mb-2">Searching...</div>
                )}

                {parentSearchResults.length > 0 && (
                  <div className="space-y-1">
                    {parentSearchResults.map((parent) => (
                      <div
                        key={parent.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {parent.first_name} {parent.last_name}
                          </div>
                          <div className="text-xs text-gray-500">{parent.email}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddParentRelationship(parent)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                          disabled={isLoading}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {parentSearch.length >= 2 && !searchingParents && parentSearchResults.length === 0 && (
                  <div className="text-sm text-gray-500">No parents found.</div>
                )}
              </div>
            )}
          </div>

          {formError && (
            <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
              {formError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// Helper function to copy student ID to clipboard
const copyStudentId = (studentId: string) => {
  navigator.clipboard.writeText(studentId);
  alert('Student ID copied to clipboard!');
};

export function AdminStudents() {
  const [activeTab, setActiveTab] = useUrlTabState<StudentsTab>('tab', STUDENTS_TABS, 'directory');

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'name' | 'school' | 'grade' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    school: '',
    grade: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState<string | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const [cleanupModal, setCleanupModal] = useState<{
    isOpen: boolean;
    studentId: string;
    studentName: string;
  }>({
    isOpen: false,
    studentId: '',
    studentName: '',
  });
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [importResults, setImportResults] = useState<{
    success: number;
    errors: string[];
    newParents: number;
    newStudents: number;
  } | null>(null);
  const [editStudentModal, setEditStudentModal] = useState<{
    isOpen: boolean;
    student: Student | null;
  }>({
    isOpen: false,
    student: null,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [waiverPrintPreview, setWaiverPrintPreview] = useState<{
    studentName: string;
    waiver: BhcsStudentWaiver;
  } | null>(null);
  const [blankWaiverStudent, setBlankWaiverStudent] = useState<string | null>(null);
  const [contactsEditorStudent, setContactsEditorStudent] = useState<Student | null>(null);
  // Who a family has authorized to collect this child, and their photos. Held at page level
  // so the desktop table and the mobile card open the same one modal.
  const [pickupContactsStudent, setPickupContactsStudent] = useState<Student | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  // `?student=<id>` (from search, or a pasted link) narrows the directory to that one child.
  const [focusedStudentId, clearFocusedStudent] = useFocusedRecordId(STUDENT_PARAM);

  // Pagination (100 per page)
  const PAGE_SIZE = 100;
  const [studentsPage, setStudentsPage] = useState<number>(1);
  const [studentsTotal, setStudentsTotal] = useState<number>(0);

  // Arriving on a link opens that child's row, and starts the pager over: a link followed
  // from page 3 of the full list should not read "201–1 of 1".
  useEffect(() => {
    if (!focusedStudentId) return;
    setSelectedStudentId(focusedStudentId);
    setStudentsPage(1);
  }, [focusedStudentId]);

  // Sort function
  const handleSort = (field: 'name' | 'school' | 'grade' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: 'name' | 'school' | 'grade' | 'created_at' }) => {
    if (sortField !== field) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortDirection === 'asc' ? 
      <span className="text-blue-600">↑</span> : 
      <span className="text-blue-600">↓</span>;
  };

  const schoolsList = getAllSchools();

  // Helper to get grade label
  const getGradeLabel = (value: string | undefined) => {
    if (!value) return null;
    const found = grades.find(g => g.value === value);
    return found ? found.label : value;
  };

  const hasAfterSchoolEnrollment = (student: Student) =>
    student.active_enrollments?.some(e => e.program_type === 'after-school') ?? false;

  const needsSchoolGradeBackfill = (student: Student) =>
    student.active &&
    hasAfterSchoolEnrollment(student) &&
    (
      !student.metadata?.school || student.metadata.school === 'Not specified' ||
      !student.metadata?.grade || student.metadata.grade === 'Not specified'
    );

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsPage, sortField, sortDirection, appliedSearchTerm, focusedStudentId]);

  async function fetchStudents() {
    try {
      setLoading(true);
      let ids: string[];
      if (focusedStudentId) {
        // A link names one child; the list is that child alone, whatever the search box says.
        ids = [focusedStudentId];
      } else {
        const offset = (studentsPage - 1) * PAGE_SIZE;
        const keywords = appliedSearchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
        // Always use server-side search to unify sorting + pagination behaviors
        const { data: idRows, error: rpcError } = await supabase.rpc('search_students', {
          p_keywords: keywords,
          p_limit: PAGE_SIZE,
          p_offset: offset,
          p_sort_field: sortField,
          p_sort_direction: sortDirection,
          p_label: null,
        });
        if (rpcError) throw rpcError;
        ids = (idRows || []).map((r: { student_id: string }) => r.student_id);
        const totalCount = (idRows && idRows[0] && typeof (idRows[0] as { total_count?: number }).total_count === 'number')
          ? (idRows[0] as { total_count: number }).total_count
          : 0;
        setStudentsTotal(totalCount);
      }
      if (ids.length === 0) {
        setStudents([]);
        setLoading(false);
        return;
      }

      const baseSelect = `
        *,
        student_parents (
          relationship,
          is_primary,
          parent:parents (
            id,
            first_name,
            last_name,
            email,
            phone,
            postal_code
          )
        )
      `;
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select(baseSelect)
        .in('id', ids);
      if (studentsError) throw studentsError;
      if (focusedStudentId) {
        setStudentsTotal(studentsData?.length ?? 0);
        // A link to a child who has since been deleted: nothing to look up enrollments for.
        if (!studentsData?.length) {
          setStudents([]);
          setLoading(false);
          return;
        }
      }

      // Preserve order from RPC
      const orderIndex = new Map<string, number>();
      ids.forEach((id, idx) => orderIndex.set(id, idx));
      const orderedStudents = [...(studentsData ?? [])].sort((a, b) => {
        const ia = orderIndex.get(a.id) ?? 0;
        const ib = orderIndex.get(b.id) ?? 0;
        return ia - ib;
      });

      // Get all student IDs
      const studentIds = orderedStudents.map(s => s.id);

      // Fetch all active enrollments for each student
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select('student_id, created_at, programs(name, type)')
        .in('student_id', studentIds)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (enrollmentsError) {
        console.error('Error fetching enrollments:', enrollmentsError);
      }

      // Create a map of all active enrollments for each student
      const allEnrollmentsMap = new Map<string, Array<{ program_name: string, program_type: string, created_at: string }>>();
      if (enrollmentsData) {
        for (const enrollment of enrollmentsData) {
          const studentId = enrollment.student_id as string;
          if (studentId) {
            if (!allEnrollmentsMap.has(studentId)) {
              allEnrollmentsMap.set(studentId, []);
            }

            // Handle both single object and array for programs
            const programsData = enrollment.programs as { name: string, type: string } | { name: string, type: string }[] | null;
            let programName = 'Unknown Program';
            let programType = 'other';
            if (Array.isArray(programsData) && programsData.length > 0) {
              programName = programsData[0].name;
              programType = programsData[0].type || 'other';
            } else if (programsData && !Array.isArray(programsData)) {
              programName = programsData.name;
              programType = programsData.type || 'other';
            }

            allEnrollmentsMap.get(studentId)!.push({
              program_name: programName,
              program_type: programType,
              created_at: enrollment.created_at as string,
            });
          }
        }
      }

      // Attach all enrollments to each student
      const studentsWithEnrollments = orderedStudents.map(student => ({
        ...student,
        active_enrollments: allEnrollmentsMap.get(student.id) || [],
      }));

      setStudents(studentsWithEnrollments || []);
      setLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching students:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
      setLoading(false);
    }
  }

  async function handleEditStudent(studentData: {
    first_name: string;
    last_name: string;
    date_of_birth: string | null;
    metadata: { school?: string; grade?: string; room_number?: string; labels?: string[]; deposit_badge?: { amount: number } };
    relationshipsToRemove: string[];
    relationshipsToAdd: Array<{
      parent_id: string;
      relationship: string;
      is_primary: boolean;
    }>;
  }) {
    if (!editStudentModal.student) return;

    try {
      setEditLoading(true);
      
      // Update student basic info
      const { error: studentError } = await supabase
        .from('students')
        .update({
          first_name: studentData.first_name,
          last_name: studentData.last_name,
          date_of_birth: studentData.date_of_birth || null,
          // Preserve other metadata keys like bhcs_waiver
          metadata: {
            ...(editStudentModal.student?.metadata || {}),
            school: studentData.metadata.school,
            grade: studentData.metadata.grade,
            room_number: studentData.metadata.room_number,
            labels: studentData.metadata.labels,
            // undefined drops the key on JSON serialization, clearing the badge
            deposit_badge: studentData.metadata.deposit_badge,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', editStudentModal.student.id);

      if (studentError) throw studentError;

      // Remove relationships
      if (studentData.relationshipsToRemove.length > 0) {
        const { error: removeError } = await supabase
          .from('student_parents')
          .delete()
          .eq('student_id', editStudentModal.student.id)
          .in('parent_id', studentData.relationshipsToRemove);

        if (removeError) throw removeError;

        // Unlinking removes a parent's access to this child's record, invoices and pickup —
        // an access-control change, and the row is deleted outright.
        analytics.trackAdminAction('student_parent_unlink', {
          entityType: 'student',
          entityId: editStudentModal.student.id,
          metadata: {
            student_name: `${studentData.first_name} ${studentData.last_name}`,
            parent_ids: studentData.relationshipsToRemove,
            count: studentData.relationshipsToRemove.length,
          },
        });
      }

      // Add new relationships
      if (studentData.relationshipsToAdd.length > 0) {
        const relationshipPromises = studentData.relationshipsToAdd.map(rel => {
          return supabase.rpc('admin_add_student_parent_relationship', {
            p_student_id: editStudentModal.student!.id,
            p_parent_id: rel.parent_id,
            p_relationship: rel.relationship,
            p_is_primary: rel.is_primary,
            p_can_pay: true // Defaulting to true for now
          });
        });

        const results = await Promise.all(relationshipPromises);
        results.forEach(result => {
          if (result.error) throw result.error;
        });

        analytics.trackAdminAction('student_parent_link', {
          entityType: 'student',
          entityId: editStudentModal.student.id,
          metadata: {
            student_name: `${studentData.first_name} ${studentData.last_name}`,
            parent_ids: studentData.relationshipsToAdd.map(rel => rel.parent_id),
            count: studentData.relationshipsToAdd.length,
          },
        });
      }

      // Close modal and refresh data
      setEditStudentModal({ isOpen: false, student: null });
      setSuccessMessage('Student updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      trackEvent({ eventType: 'student_update', user: currentUser, userType: 'admin', entityType: 'student', entityId: editStudentModal.student.id, metadata: { student_name: `${studentData.first_name} ${studentData.last_name}` } });
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error updating student:', error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setEditLoading(false);
    }
  }

  async function handleAddStudent(e: React.FormEvent) {
    e.preventDefault();
    try {
      setFormError(null);

      // Basic validation
      if (!newStudent.first_name || !newStudent.last_name) {
        setFormError('First name and last name are required');
        return;
      }
      if (!newStudent.school) {
        setFormError('School is required');
        return;
      }
      if (!newStudent.grade) {
        setFormError('Grade is required');
        return;
      }

      // Add the student
      const { data: created, error } = await supabase.from('students').insert([
        {
          first_name: newStudent.first_name,
          last_name: newStudent.last_name,
          date_of_birth: newStudent.date_of_birth || null,
          active: true,
          metadata: { school: newStudent.school, grade: newStudent.grade },
        },
      ]).select('id');

      if (error) throw error;

      trackEvent({
        eventType: 'student_create',
        user: currentUser,
        userType: 'admin',
        entityType: 'student',
        entityId: (created?.[0] as { id?: string } | undefined)?.id,
        metadata: {
          action: 'student_created',
          student_name: `${newStudent.first_name} ${newStudent.last_name}`,
          student_school: newStudent.school,
          student_grade: newStudent.grade,
          source: 'admin_students_page',
        },
      });

      // Reset form and fetch updated students
      setNewStudent({ first_name: '', last_name: '', date_of_birth: '', school: '', grade: '' });
      setIsAddingStudent(false);
      setSuccessMessage('Student added successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error adding student:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function handleWaiverOptOut(student: Student) {
    if (!window.confirm(`Mark waiver as opted-out for ${student.first_name} ${student.last_name}?`)) return;
    try {
      const { error } = await supabase
        .from('students')
        .update({
          metadata: {
            ...(student.metadata || {}),
            bhcs_waiver: { opt_out: true, optedOutAt: new Date().toISOString() },
          },
        })
        .eq('id', student.id);
      if (error) throw error;
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error opting out waiver:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  }

  // Marker only — this does not change invoice generation. Invoices are still
  // produced at full price so they can be preflighted, then zeroed on demand
  // via the Comp action in Finances (or comp_invoices_batch).
  async function handleToggleNoCharge(student: Student) {
    const next = !student.no_charge;
    const name = `${student.first_name} ${student.last_name}`;
    const message = next
      ? `Mark ${name} as No Charge? Invoices will still be generated at full price — you comp them from Finances → Invoices, or run the no-charge batch after generating.`
      : `Remove the No Charge marker from ${name}? Future invoices will be collected normally. Already-comped invoices are not affected.`;
    if (!window.confirm(message)) return;

    try {
      const { error } = await supabase
        .from('students')
        .update({ no_charge: next })
        .eq('id', student.id);

      if (error) throw error;
      trackEvent({
        eventType: 'student_no_charge_toggle',
        user: currentUser,
        userType: 'admin',
        entityType: 'student',
        entityId: student.id,
        metadata: { student_name: name, no_charge: next },
      });
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error toggling no-charge marker:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function handleToggleStudentStatus(
    studentId: string,
    currentStatus: boolean,
    studentName?: string,
  ) {
    try {
      const { error } = await supabase
        .from('students')
        .update({ active: !currentStatus })
        .eq('id', studentId);

      if (error) throw error;
      // The admin overview's "Total Students" 24h delta counts these events. It used to
      // infer deactivations from students.updated_at, which any bulk write to the table
      // would trip; this toggle is the only place a student goes inactive from the UI.
      trackEvent({
        eventType: 'admin_student_status_toggle',
        user: currentUser,
        userType: 'admin',
        entityType: 'student',
        entityId: studentId,
        metadata: {
          ...(studentName ? { student_name: studentName } : {}),
          action: currentStatus ? 'deactivate' : 'activate',
        },
      });
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error toggling student status:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function handleCleanupStudent() {
    const { studentId: deletedStudentId, studentName: deletedStudentName } = cleanupModal;
    try {
      setCleanupLoading(cleanupModal.studentId);
      setCleanupError(null);

      // Due to cascade delete in the database, we only need to delete the student
      // This will automatically delete all associated records:
      // - student_parents (CASCADE)
      // - enrollments (CASCADE)
      // - invoices (CASCADE from enrollments)
      // - payments (CASCADE from invoices)
      // - refunds (CASCADE from payments)
      // - stripe_transactions (CASCADE from payments)
      // - invoicing_line_items (CASCADE from invoices)
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('id', cleanupModal.studentId);

      if (error) throw error;

      setSuccessMessage(`${deletedStudentName} and all associated data have been deleted successfully!`);
      setTimeout(() => setSuccessMessage(null), 5000);
      trackEvent({ eventType: 'student_delete', user: currentUser, userType: 'admin', entityType: 'student', entityId: deletedStudentId, metadata: { student_name: deletedStudentName } });
      fetchStudents();
    } catch (error: unknown) {
      console.error('Error cleaning up student:', error);
      const errorObj = error as { message?: string };
      setCleanupError(errorObj.message || 'An unknown error occurred');
    } finally {
      setCleanupLoading(null);
      setCleanupModal({ isOpen: false, studentId: '', studentName: '' });
    }
  }

  const handleCsvImportSuccess = (results: typeof importResults) => {
    setImportResults(results);
    setIsCsvImportOpen(false);
    
    // Show success message
    const message = `CSV import completed: ${results?.success || 0} students added, ${results?.newParents || 0} new guardians created.`;
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
      setImportResults(null); // Clear import results after success message disappears
    }, 10000);
    
    // Refresh the students list
    fetchStudents();
  };

  // All filtering and sorting are performed on the server now.
  const applyFilters = () => {
    // Searching means "show me the list again", so it lets go of a linked child.
    if (focusedStudentId) clearFocusedStudent();
    setStudentsPage(1);
    setAppliedSearchTerm(searchTerm.trim());
  };

  if (error) {
    return (
      <AdminLayout>
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  if (waiverPrintPreview) {
    return (
      <WaiverPrintPreview
        studentName={waiverPrintPreview.studentName}
        waiver={waiverPrintPreview.waiver}
        onBack={() => setWaiverPrintPreview(null)}
      />
    );
  }

  if (blankWaiverStudent) {
    return (
      <BlankWaiverPrintPreview
        studentName={blankWaiverStudent}
        onBack={() => setBlankWaiverStudent(null)}
      />
    );
  }

  if (activeTab === 'roster') {
    return (
      <AdminLayout>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
          <StudentsTabs active={activeTab} onChange={setActiveTab} />
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
            <AdminPickupRoster />
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Who is coming on one date, across every program meeting that day — after-school,
  // enrichment and camp. The planning counterpart to the pickup board's current week.
  if (activeTab === 'day') {
    return (
      <AdminLayout>
        {/* Clipping on desktop only: an `overflow-hidden` ancestor stops the program strip
            from sticking under the phone's top bar, and on a phone there is no rounded
            corner for it to clip anyway. */}
        <div className="bg-white shadow lg:overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
          <StudentsTabs active={activeTab} onChange={setActiveTab} />
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto bg-gray-50">
            <AdminDayRoster />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (activeTab === 'history') {
    return (
      <AdminLayout>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
          <StudentsTabs active={activeTab} onChange={setActiveTab} />
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto bg-gray-50">
            <AdminAttendanceHistory />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (activeTab === 'photos') {
    return (
      <AdminLayout>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
          <StudentsTabs active={activeTab} onChange={setActiveTab} />
          <div className="p-4 sm:p-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto bg-gray-50">
            <StudentPhotoImport />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <StudentsTabs active={activeTab} onChange={setActiveTab} />
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1 sm:flex-none sm:w-80">
              <input
                type="text"
                placeholder="Search by name or school..."
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyFilters();
                  }
                }}
              />
            </div>
            <button
              onClick={() => setIsAddingStudent(true)}
              className="sm:hidden w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl leading-none hover:bg-blue-700 transition-colors flex-shrink-0"
              aria-label="Add student"
            >
              +
            </button>
            <button
              onClick={() => setIsAddingStudent(true)}
              className="hidden sm:block px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
            >
              Add Student
            </button>
          </div>
        </div>

        {focusedStudentId && (
          <FocusedRecordBanner
            noun="student"
            loading={loading}
            found={students.length > 0}
            onShowAll={clearFocusedStudent}
          />
        )}

        {successMessage && (
          <div className="m-4 p-4 bg-green-50 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}

        {cleanupError && (
          <div className="m-4 p-4 bg-red-50 text-red-600 rounded-md">
            {cleanupError}
          </div>
        )}

        {importResults && importResults.errors.length > 0 && (
          <div className="m-4 p-4 bg-yellow-50 text-yellow-700 rounded-md">
            <h4 className="font-medium mb-2">Import Warnings:</h4>
            <ul className="list-disc list-inside space-y-1">
              {importResults.errors.map((error, index) => (
                <li key={index} className="text-sm">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {(() => {
          const count = students.filter(needsSchoolGradeBackfill).length;
          if (count === 0) return null;
          return (
            <div className="mx-4 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
              <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9888;</span>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{count} after-school student{count > 1 ? 's are' : ' is'} missing school or grade.</span>
                {' '}Please edit each highlighted row to backfill this info before the new school year re-enrollment.
              </p>
            </div>
          );
        })()}

        {isAddingStudent && (
          <div className="p-4 border-b border-gray-200">
            <h4 className="text-md font-medium text-gray-700 mb-4">Add New Student</h4>
            <form onSubmit={handleAddStudent} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newStudent.first_name}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, first_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newStudent.last_name}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, last_name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="date_of_birth"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newStudent.date_of_birth}
                    onChange={(e) =>
                      setNewStudent({ ...newStudent, date_of_birth: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="new_school" className="block text-sm font-medium text-gray-700">
                    School <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="new_school"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newStudent.school}
                    onChange={(e) => setNewStudent({ ...newStudent, school: e.target.value })}
                  >
                    <option value="">Select a school</option>
                    {schoolsList.map((school: string) => (
                      <option key={school} value={school}>{school}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="new_grade" className="block text-sm font-medium text-gray-700">
                    Grade <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="new_grade"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    value={newStudent.grade}
                    onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}
                  >
                    <option value="">Select a grade</option>
                    {grades.map((grade) => (
                      <option key={grade.value} value={grade.value}>{grade.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddingStudent(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Student
                </button>
              </div>
            </form>
            {formError && (
              <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-md">
                {formError}
              </div>
            )}
          </div>
        )}

        {/* Top Pagination */}
        <div className="p-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
          {(() => {
            const totalPages = Math.max(1, Math.ceil(studentsTotal / PAGE_SIZE));
            const start = (studentsPage - 1) * PAGE_SIZE + 1;
            const end = Math.min(studentsPage * PAGE_SIZE, studentsTotal);
            return (
              <>
                <div className="text-sm text-gray-600 flex items-center gap-1.5">
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                      Loading students...
                    </>
                  ) : (
                    `Showing ${studentsTotal === 0 ? 0 : start} to ${end} of ${studentsTotal} students`
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setStudentsPage(1)}
                    disabled={studentsPage === 1}
                  >
                    First
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setStudentsPage(Math.max(1, studentsPage - 1))}
                    disabled={studentsPage === 1}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {studentsPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setStudentsPage(Math.min(totalPages, studentsPage + 1))}
                    disabled={studentsPage === totalPages}
                  >
                    Next
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setStudentsPage(totalPages)}
                    disabled={studentsPage === totalPages}
                  >
                    Last
                  </button>
                </div>
              </>
            );
          })()}
        </div>

        <div className="overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
          {/* Desktop Table */}
          <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>NAME</span>
                    <SortIcon field="name" />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Student Info
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Waiver
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  PARENTS
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  ACTIVE ENROLLMENTS
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  STATUS
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>ADDED</span>
                    <SortIcon field="created_at" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.map((student) => (
                <React.Fragment key={student.id}>
                <tr className={selectedStudentId === student.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} onClick={() => setSelectedStudentId(selectedStudentId === student.id ? null : student.id)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {student.first_name} {student.last_name}
                    {student.no_charge && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium"
                        title="Never charged. Invoices are still generated at full price, then comped."
                      >
                        No charge
                      </span>
                    )}
                    {isFosterCityResident(student) && <FosterCityResidentBadge className="ml-2" />}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {student.date_of_birth && (
                        <div className="text-sm text-gray-900">
                          DOB: {formatDateDisplay(student.date_of_birth)}
                        </div>
                      )}
                      {student.metadata?.school && student.metadata.school !== 'Not specified' && (
                        <div className="text-sm text-gray-900">
                          School: {student.metadata.school}
                        </div>
                      )}
                      {student.metadata?.grade && student.metadata.grade !== 'Not specified' && (
                        <div className="text-sm text-gray-900">
                          Grade: {getGradeLabel(student.metadata.grade)}
                        </div>
                      )}
                      {student.metadata?.room_number && (
                        <div className="text-sm text-gray-900">
                          Room: {student.metadata.room_number}
                        </div>
                      )}
                      {student.metadata?.labels && student.metadata.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {student.metadata.labels.map((label) => (
                            <span key={label} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      {typeof student.metadata?.deposit_badge?.amount === 'number' && student.metadata.deposit_badge.amount > 0 && (
                        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          💰 Deposit ${student.metadata.deposit_badge.amount.toFixed(2)}
                        </div>
                      )}
                      {!student.date_of_birth && (!student.metadata?.school || student.metadata.school === 'Not specified') && (!student.metadata?.grade || student.metadata.grade === 'Not specified') && (!student.metadata?.labels || student.metadata.labels.length === 0) && (
                        <div className="text-sm text-gray-500">No student info available</div>
                      )}
                      {needsSchoolGradeBackfill(student) && (
                        <div className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-medium text-amber-700">
                          &#9888; School/grade needed
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const waiverUnknown = student.metadata?.bhcs_waiver;
                      if (isBhcsStudentWaiverOptOut(waiverUnknown)) {
                        return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">Opt-out</span>;
                      }
                      if (isBhcsStudentWaiverSigned(waiverUnknown) || waiverUnknown) {
                        return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">Signed</span>;
                      }
                      return <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">Not signed</span>;
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {student.student_parents?.map((sp, index) => (
                        <div key={index} className="text-sm">
                          <div className="font-medium text-gray-900">
                            {sp.parent.first_name} {sp.parent.last_name}
                            {sp.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                          </div>
                          <div className="text-gray-500">
                            {sp.parent.email}
                          </div>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {student.active_enrollments && student.active_enrollments.length > 0 ? (
                      <div className="space-y-0.5">
                        {student.active_enrollments.map((enrollment, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <span className="text-base" title={enrollment.program_type}>
                              {getProgramTypeIcon(enrollment.program_type)}
                            </span>
                            <div className="text-sm text-gray-900">
                              {enrollment.program_name}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No enrollments</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      student.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {student.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatDateInBusinessTZ(student.created_at)}
                  </td>
                </tr>
                {selectedStudentId === student.id && (
                  <tr key={`${student.id}-actions`} className="bg-blue-50">
                    <td colSpan={7} className="px-6 py-2 border-t border-blue-100">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyStudentId(student.id); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                          title="Copy student ID to clipboard"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy ID
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditStudentModal({ isOpen: true, student }); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setPickupContactsStudent(student); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                          title="Authorized pickup contacts and their photos"
                        >
                          <UserCheck className="h-3 w-3 mr-1" />
                          Pickup contacts
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleStudentStatus(student.id, student.active, `${student.first_name} ${student.last_name}`); }}
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${
                            student.active
                              ? 'text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900'
                              : 'text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900'
                          }`}
                        >
                          {student.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                          {student.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleNoCharge(student); }}
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                            student.no_charge
                              ? 'text-purple-700 bg-purple-100 border-purple-300 hover:bg-purple-200'
                              : 'text-gray-600 bg-gray-100 border-gray-300 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                          title={student.no_charge
                            ? 'Remove the No Charge marker'
                            : 'Mark as No Charge (owner/staff family). Invoices are still generated at full price, then comped.'}
                        >
                          <Gift className="h-3 w-3 mr-1" />
                          {student.no_charge ? 'No Charge ✓' : 'No Charge'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCleanupModal({ isOpen: true, studentId: student.id, studentName: `${student.first_name} ${student.last_name}` }); }}
                          disabled={!!cleanupLoading}
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors ${cleanupLoading === student.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Clean up
                        </button>
                        {(() => {
                          const w = student.metadata?.bhcs_waiver;
                          if (isBhcsStudentWaiverSigned(w)) {
                            const studentName = `${student.first_name} ${student.last_name}`;
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); setWaiverPrintPreview({ studentName, waiver: w }); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Preview and download waiver PDF"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Download PDF
                              </button>
                            );
                          }
                          if (!w || isBhcsStudentWaiverOptOut(w)) {
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBlankWaiverStudent(`${student.first_name} ${student.last_name}`); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                  title="Print blank waiver form"
                                >
                                  <Printer className="h-3 w-3 mr-1" />
                                  Print blank
                                </button>
                                {!w && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleWaiverOptOut(student); }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                    title="Mark waiver as opted-out"
                                  >
                                    Opt-out
                                  </button>
                                )}
                              </>
                            );
                          }
                          return null;
                        })()}
                        <button
                          onClick={(e) => { e.stopPropagation(); setContactsEditorStudent(student); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          title="Update guardian and emergency contact info"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Update contacts
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="lg:hidden">
            {students.map((student) => (
              <div key={student.id} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedStudentId === student.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedStudentId(selectedStudentId === student.id ? null : student.id)}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="text-base font-bold text-gray-900 flex items-center flex-wrap gap-2">
                      <span>{student.first_name} {student.last_name}</span>
                      {student.no_charge && (
                        <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">No charge</span>
                      )}
                      {isFosterCityResident(student) && <FosterCityResidentBadge />}
                      {(() => {
                        const waiverUnknown = student.metadata?.bhcs_waiver;
                        if (isBhcsStudentWaiverOptOut(waiverUnknown)) {
                          return <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">Waiver opt-out</span>;
                        }
                        if (isBhcsStudentWaiverSigned(waiverUnknown) || waiverUnknown) {
                          return <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">Waiver signed</span>;
                        }
                        return <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-xs font-medium">Waiver not signed</span>;
                      })()}
                      <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        student.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {student.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <span>{student.metadata?.school || 'No School'}</span>
                      <span className="mx-1.5">|</span>
                      <span>{getGradeLabel(student.metadata?.grade) || 'No Grade'}</span>
                      <span className="mx-1.5">|</span>
                      <span>{student.date_of_birth ? formatDateDisplay(student.date_of_birth) : 'Not provided'}</span>
                  {student.metadata?.room_number && (
                    <>
                      <span className="mx-1.5">|</span>
                      <span>Room {student.metadata.room_number}</span>
                    </>
                  )}
                  {student.metadata?.labels && student.metadata.labels.length > 0 && (
                    <>
                      <span className="mx-1.5">|</span>
                      <span>{student.metadata.labels.join(', ')}</span>
                    </>
                  )}
                    </div>
                    {needsSchoolGradeBackfill(student) && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-medium text-amber-700">
                        &#9888; School/grade needed
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <div>
                    <span className="font-medium text-gray-500">Parents: </span>
                    {student.student_parents?.length > 0 ? student.student_parents.map((sp, index) => (
                      <span key={index} className="text-gray-800">
                        {sp.parent.first_name} {sp.parent.last_name}{sp.is_primary ? ' (P)' : ''}{index < student.student_parents.length - 1 ? ', ' : ''}
                      </span>
                    )) : <span className="text-gray-500">None</span>}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Active Enrollments: </span>
                    {student.active_enrollments && student.active_enrollments.length > 0 ? (
                      <div className="text-gray-800">
                        {student.active_enrollments.map((enrollment, index) => (
                          <span key={index} className="inline-flex items-center space-x-1">
                            <span title={enrollment.program_type}>
                              {getProgramTypeIcon(enrollment.program_type)}
                            </span>
                            <span>{enrollment.program_name}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </div>
                </div>

                {selectedStudentId === student.id && (
                  <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => copyStudentId(student.id)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                      title="Copy student ID to clipboard"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy ID
                    </button>
                    <button
                      onClick={() => setEditStudentModal({ isOpen: true, student })}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => setPickupContactsStudent(student)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                    >
                      <UserCheck className="h-3 w-3 mr-1" />
                      Pickup contacts
                    </button>
                    <button
                      onClick={() => handleToggleStudentStatus(student.id, student.active, `${student.first_name} ${student.last_name}`)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium transition-colors ${
                        student.active
                          ? 'text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900'
                          : 'text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900'
                      }`}
                    >
                      {student.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                      {student.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleToggleNoCharge(student)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                        student.no_charge
                          ? 'text-purple-700 bg-purple-100 border-purple-300 hover:bg-purple-200'
                          : 'text-gray-600 bg-gray-100 border-gray-300 hover:bg-gray-200 hover:text-gray-900'
                      }`}
                    >
                      <Gift className="h-3 w-3 mr-1" />
                      {student.no_charge ? 'No Charge ✓' : 'No Charge'}
                    </button>
                    <button
                      onClick={() => setCleanupModal({ isOpen: true, studentId: student.id, studentName: `${student.first_name} ${student.last_name}` })}
                      disabled={!!cleanupLoading}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors ${cleanupLoading === student.id ? 'opacity-50' : ''}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Clean up
                    </button>
                    {(() => {
                      const w = student.metadata?.bhcs_waiver;
                      if (isBhcsStudentWaiverSigned(w)) {
                        const studentName = `${student.first_name} ${student.last_name}`;
                        return (
                          <button
                            onClick={() => setWaiverPrintPreview({ studentName, waiver: w })}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                            title="Preview and download waiver PDF"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download PDF
                          </button>
                        );
                      }
                      if (!w || isBhcsStudentWaiverOptOut(w)) {
                        return (
                          <>
                            <button
                              onClick={() => setBlankWaiverStudent(`${student.first_name} ${student.last_name}`)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                              title="Print blank waiver form"
                            >
                              <Printer className="h-3 w-3 mr-1" />
                              Print blank
                            </button>
                            {!w && (
                              <button
                                onClick={() => handleWaiverOptOut(student)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Mark waiver as opted-out"
                              >
                                Opt-out
                              </button>
                            )}
                          </>
                        );
                      }
                      return null;
                    })()}
                    <button
                      onClick={() => setContactsEditorStudent(student)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                      title="Update guardian and emergency contact info"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Update contacts
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!loading && students.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No students found.
              </p>
            </div>
          )}
        </div>

      </div>

      <CleanupModal
        isOpen={cleanupModal.isOpen}
        onClose={() => setCleanupModal({ isOpen: false, studentId: '', studentName: '' })}
        onConfirm={handleCleanupStudent}
        studentName={cleanupModal.studentName}
        isLoading={!!cleanupLoading}
      />

      <CsvImport
        isOpen={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onSuccess={handleCsvImportSuccess}
      />

      <EditStudentModal
        isOpen={editStudentModal.isOpen}
        onClose={() => setEditStudentModal({ isOpen: false, student: null })}
        student={editStudentModal.student}
        onSave={handleEditStudent}
        isLoading={editLoading}
      />

      {contactsEditorStudent && (
        <StudentContactsEditor
          studentId={contactsEditorStudent.id}
          studentName={`${contactsEditorStudent.first_name} ${contactsEditorStudent.last_name}`}
          waiver={(contactsEditorStudent.metadata?.bhcs_waiver ?? null) as BhcsWaiverMetadata | null}
          user={currentUser}
          userType="admin"
          onClose={() => setContactsEditorStudent(null)}
          onSaved={() => {
            fetchStudents();
          }}
        />
      )}

      {pickupContactsStudent && (
        <PickupContactsModal
          studentId={pickupContactsStudent.id}
          studentName={`${pickupContactsStudent.first_name} ${pickupContactsStudent.last_name}`}
          onClose={() => setPickupContactsStudent(null)}
        />
      )}
    </AdminLayout>
  );
} 